// src/CategoryManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  collection,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  setDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import CategoryNotesManager from './CategoryNotesManager';

const CATEGORY_COLLECTION = 'recipe_categories';

export default function CategoryManager() {
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ id: '', name: '', description: '', types: [] });
  const [showForm, setShowForm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedForNotes, setSelectedForNotes] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const snap = await getDocs(collection(db, CATEGORY_COLLECTION));
    const cats = snap.docs.map(d => ({ docId: d.id, ...d.data() }));
    cats.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setCategories(cats);
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.id || !form.name) return;
    const data = {
      ...form,
      order: editing ? editing.order : categories.length,
      archived: editing ? editing.archived : false
    };
    const ref = doc(db, CATEGORY_COLLECTION, form.id);
    if (editing) await updateDoc(ref, data);
    else await setDoc(ref, data);
    setForm({ id: '', name: '', description: '', types: [] });
    setEditing(null);
    setShowForm(false);
    fetchCategories();
  };

  const handleEdit = cat => {
    setEditing(cat);
    setForm({ id: cat.docId, name: cat.name, description: cat.description || '', types: cat.types || [] });
    setShowForm(true);
  };

  const handleArchive = cat => setConfirmArchive(cat);
  const applyArchive = async () => {
    await updateDoc(doc(db, CATEGORY_COLLECTION, confirmArchive.docId), { archived: true });
    setConfirmArchive(null);
    setSnackbar({ open: true, message: 'Archived', severity: 'info' });
    fetchCategories();
  };

  const handleRestore = async cat => {
    await updateDoc(doc(db, CATEGORY_COLLECTION, cat.docId), { archived: false });
    setSnackbar({ open: true, message: 'Restored', severity: 'success' });
    fetchCategories();
  };

  const handleDelete = cat => setConfirmDelete(cat);
  const applyDelete = async () => {
    await deleteDoc(doc(db, CATEGORY_COLLECTION, confirmDelete.docId));
    setConfirmDelete(null);
    setSnackbar({ open: true, message: 'Deleted', severity: 'error' });
    fetchCategories();
  };

  const swapOrder = async (i, j) => {
    if (j < 0 || j >= categories.length) return;
    const a = visibleCats[i];
    const b = visibleCats[j];
    await updateDoc(doc(db, CATEGORY_COLLECTION, a.docId), { order: b.order });
    await updateDoc(doc(db, CATEGORY_COLLECTION, b.docId), { order: a.order });
    fetchCategories();
  };

  const onDragEnd = async result => {
    if (!result.destination) return;
    const src = result.source.index;
    const dest = result.destination.index;
    if (src !== dest) await swapOrder(src, dest);
  };

    const visibleCats = categories
    .filter(cat => showArchived || !cat.archived)
    .filter(cat => filterType === 'all' || (cat.types || []).includes(filterType))
    .filter(cat => !searchTerm || cat.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>📂 Category Manager</Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button variant="contained" onClick={() => { setShowForm(true); setEditing(null); }}>
          {editing ? 'Edit Category' : 'Add New Category'}
        </Button>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={<Switch checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />}
            label="Show Archived"
          />
          <FormControl size="small">
            <InputLabel id="filter-type-label">Type</InputLabel>
            <Select
              labelId="filter-type-label"
              value={filterType}
              label="Type"
              onChange={e => setFilterType(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="beverage">Beverage</MenuItem>
              <MenuItem value="cooking">Cooking</MenuItem>
              <MenuItem value="dessert">Dessert</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {showForm && (
        <Paper sx={{ p: 3, mb: 4 }} elevation={3}>
          <Stack spacing={2}>
            <TextField label="Category ID" name="id" value={form.id} onChange={handleFormChange} disabled={!!editing} />
            <TextField label="Category Name" name="name" value={form.name} onChange={handleFormChange} />
            <TextField label="Description" name="description" value={form.description} onChange={handleFormChange} multiline minRows={2} />
            <FormControl fullWidth>
              <InputLabel id="types-label">Recipe Types</InputLabel>
              <Select
                labelId="types-label"
                multiple
                name="types"
                value={form.types}
                onChange={e => setForm(prev => ({
                  ...prev,
                  types: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                }))}
                renderValue={selected => selected.join(', ')}
              >
                {['beverage', 'cooking', 'dessert'].map(type => (
                  <MenuItem key={type} value={type}>
                    <Checkbox checked={form.types.includes(type)} />
                    <ListItemText primary={type.charAt(0).toUpperCase() + type.slice(1)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={() => { setShowForm(false); setEditing(null); setForm({ id: '', name: '', description: '', types: [] }); }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSave}>
                {editing ? 'Update Category' : 'Add Category'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Box mb={2}>
        <TextField
          fullWidth
          placeholder="Search Categories"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          margin="normal"
        />
      </Box>
      <Typography variant="h6" gutterBottom>📋 Existing Categories</Typography>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="cats">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {visibleCats.map((cat, idx) => (
                <Draggable key={cat.docId} draggableId={cat.docId} index={idx}>
                  {providedDraggable => (
                    <Box>
                      <Paper
                        ref={providedDraggable.innerRef}
                        {...providedDraggable.draggableProps}
                        sx={{ p: 2, mb: 1 }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center">
                            <Box {...providedDraggable.dragHandleProps} sx={{ mr: 1, cursor: 'grab' }}>⇅</Box>
                            <Typography>
                              {cat.name}
                              {cat.archived && <span style={{ color: 'red', marginLeft: 8 }}>(Archived)</span>}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            {!cat.archived ? (
                              <Button size="small" color="warning" onClick={() => handleArchive(cat)}>
                                Archive
                              </Button>
                            ) : (
                              <>
                                <Button size="small" onClick={() => handleRestore(cat)}>
                                  Restore
                                </Button>
                                <Button size="small" color="error" onClick={() => handleDelete(cat)}>
                                  Delete
                                </Button>
                              </>
                            )}
                            <Button size="small" onClick={() => handleEdit(cat)}>
                              Edit
                            </Button>
                            <Button
                              size="small"
                              onClick={() =>
                                setSelectedForNotes(
                                  selectedForNotes === cat.docId ? null : cat.docId
                                )
                              }
                            >
                              {selectedForNotes === cat.docId ? 'Hide Notes' : 'Manage Notes'}
                            </Button>
                          </Stack>
                        </Box>
                      </Paper>
                      {selectedForNotes === cat.docId && (
                        <Box sx={{ mb: 2, ml: 4 }}>
                          <CategoryNotesManager categoryId={cat.docId} />
                        </Box>
                      )}
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Confirmation Dialogs */}
      <Dialog open={!!confirmArchive} onClose={() => setConfirmArchive(null)}>
        <DialogTitle>Confirm Archive</DialogTitle>
        <DialogContent>Archive this category?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmArchive(null)}>Cancel</Button>
          <Button color="warning" onClick={applyArchive}>Archive</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Delete permanently?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" onClick={applyDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}
